import { Col, Row } from '@/components/grid/grid-component';
import { BodyText, Subtitle2Text } from '@/utils/global-fonts';
import { BsInfoCircle, BsPencilSquare } from 'react-icons/bs';
import { MdOutlineNotInterested, MdPlaylistAddCheck } from 'react-icons/md';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Tooltip from '@/components/tooltip/tooltip-component';

const EditIcon = styled(BsPencilSquare)`
  color: ${({ theme }) => theme.COLOR_TEXT};
  cursor: pointer;
`;

const StyledCol = styled(Col)`
  align-self: center;
`;

const StyledCenterCol = styled(Col)`
  align-self: center;
  display: flex;
  align-items: center;
`;

const InfoIcon = styled(BsInfoCircle)`
  margin-right: 16px;
  color: ${({ theme }) => theme.COLOR_TEXT};
  cursor: pointer;
`;

const CategoryDescription = styled(BodyText)`
  width: 100%;
`;

const NoImageIcon = styled(MdOutlineNotInterested)`
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.COLOR_GREY4};
`;

const Image = styled.img`
  height: 40px;
  margin-right: 16px;
  object-fit: cover;
  width: 60px;
  border: 1px solid ${({ theme }) => theme.COLOR_GREY2};
  border-radius: 4px;
`;

const NoImage = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  margin-right: 16px;
  height: 40px;
  width: 60px;
  border: 1px solid ${({ theme }) => theme.COLOR_GREY2};
  border-radius: 4px;
`;

const CategoryRow = styled(Row)`
  margin: 16px 0px !important;
  display: flex;
`;

const GreyLine = styled.div`
  display: flex;
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.COLOR_GREY5};
  margin-top: auto;
`;

const OverTheLineIconContainer = styled.span`
  padding: 2px;
  border-radius: 50%;
  background: ${(props) =>
    props.isBelowTheLine ? props.theme.COLOR_BLACK : props.theme.COLOR_GREY5};
  height: 32px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 6%;
  right: 6px;
  cursor: pointer;

  svg {
    fill: ${(props) =>
      props.isBelowTheLine ? props.theme.COLOR_WHITE : props.theme.COLOR_GREY3};
    position: absolute;
    left: 50%;
    right: 50%;
    transform: translate(-50%, -50%);
  }

  @media (hover: hover) {
    &:hover {
      background: ${(props) => props.theme.COLOR_BLACK};

      svg {
        fill: ${(props) =>
          props.isBelowTheLine
            ? props.theme.COLOR_WHITE
            : props.theme.COLOR_GREY5};
      }
    }
  }
`;

/**
 * Component that shows the Category Summaries
 * @param {object} props The component props.
 * @param {string} props.heading The heading of the Category.
 * @param {Array } props.data The data of the Category.
 * @param {Function} props.onAction The action function of the Category.
 * @param {Function} props.onInfo The info function of the Category.
 * @returns {JSX.Element} The rendered Category Summary component.
 */
const CategorySummary = ({ heading, onAction, onInfo, data }) => (
  <>
    <StyledCol>
      <Subtitle2Text>{heading}</Subtitle2Text>
    </StyledCol>
    {data?.map((item) => (
      <CategoryRow key={item.id}>
        <StyledCenterCol sm={6} md={6} lg={6} xl={6} xxl={6}>
          {item.image ? (
            <Image src={item.image} alt={item.title} />
          ) : (
            <NoImage>
              <NoImageIcon />
            </NoImage>
          )}
          <CategoryDescription>{item.title}</CategoryDescription>
        </StyledCenterCol>
        <StyledCol sm={3} md={3} lg={3} xl={3} xxl={3}>
          <BodyText>{item.partNumber}</BodyText>
          {item.belowTheLine && (
            <OverTheLineIconContainer isBelowTheLine>
              <Tooltip
                icon={<MdPlaylistAddCheck size={20} />}
                id={`${item.id}_belowTheLine`}
                text="Below The Line"
              />
            </OverTheLineIconContainer>
          )}
        </StyledCol>
        <StyledCol sm={3} md={3} lg={3} xl={3} xxl={3}>
          <InfoIcon onClick={() => onInfo(item)} />
          <EditIcon onClick={() => onAction(item.id)} />
        </StyledCol>
      </CategoryRow>
    ))}

    <GreyLine />
  </>
);
CategorySummary.defaultProps = {
  onAction: () => {},
  onInfo: () => {},
  data: [
    {
      id: undefined,
      title: 'N/A',
      image: '',
      description: 'N/A',
      partNumber: '-',
    },
  ],
};

CategorySummary.propTypes = {
  heading: PropTypes.string.isRequired,
  onAction: PropTypes.func,
  onInfo: PropTypes.func,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      image: PropTypes.string,
      description: PropTypes.string,
      partNumber: PropTypes.string,
    }),
  ),
};

export default CategorySummary;
